import gsap from 'gsap';
import { throttle } from 'throttle-debounce';

import { isScreenAtLeast } from '../../common/utils';
import pageContent from '../page-content';

const els = {
    $sidebar: () => $('[data-sidebar]'),
    $content: () => els.$sidebar().find('[data-sidebar-content]'),
    $face: () => els.$sidebar().find('[data-sidebar-face]'),
    $toggle: () => els.$sidebar().find('[data-sidebar-toggle]'),
    $toggleBars: () => els.$sidebar().find('[data-sidebar-toggle-bar]'),
};

const isInDrawerMode = () => (
    !isScreenAtLeast('lg')
);

const isOpen = () => (
    els.$sidebar().attr('data-open') === '1'
);

const open = async ({ duration = 0.5 } = {}) => {
    if (isOpen()) {
        return;
    }

    const $pageContent = pageContent.els.$content();
    const $sidebar = els.$sidebar();
    const $content = els.$content();
    const $face = els.$face();
    const $bars = els.$toggleBars();

    $sidebar.attr('data-open', '1');

    gsap.set($content, { visibility: 'visible'});

    if (isInDrawerMode()) {
        gsap.set(
            $pageContent,
            {
                flexShrink: 0,
                width: (
                    $(window).width()
                    - $face.outerWidth()
                ),
            }
        );
    }

    const drawerTween = (
        gsap.to(
            $sidebar,
            {
                marginLeft: 0,
                duration,
            }
        )
    );

    const barsTimeline = gsap.timeline({
        paused: true,
    });

    barsTimeline.to(
        $bars,
        { top: 6 }
    );

    barsTimeline.to(
        $bars.eq(0),
        { rotate: 45 }
    );

    barsTimeline.to(
        $bars.eq(1),
        { rotate: -45 },
        '<'
    );

    barsTimeline.duration(duration);

    await Promise.all([
        drawerTween,
        barsTimeline.play(),
    ]);
};

const openImmediately = () => {
    open({ duration: 0 });
};

const close = async ({ duration = 0.5 } = {}) => {
    if (!isOpen()) {
        return;
    }

    const $pageContent = pageContent.els.$content();
    const $sidebar = els.$sidebar();
    const $content = els.$content();
    const $bars = els.$toggleBars();

    $sidebar.attr('data-open', '0');

    const drawerTimeline = gsap.timeline({
        paused: true,
    })

    drawerTimeline.to(
        $sidebar,
        {
            marginLeft: -$content.outerWidth(),
        }
    );

    drawerTimeline.set($content, { visibility: 'hidden'});
    drawerTimeline.set($pageContent, { clearProps: 'all' });

    drawerTimeline.duration(duration);

    const barsTimeline = gsap.timeline({
        paused: true,
    })

    barsTimeline.to($bars, { rotate: 0 });

    barsTimeline.set($bars, { clearProps: 'top' });

    barsTimeline.from(
        $bars,
        {
            top: 6,
            immediateRender: false,
        }
    );

    barsTimeline.duration(duration);

    await Promise.all([
        drawerTimeline.play(),
        barsTimeline.play(),
    ]);
};

const closeImmediately = () => {
    close({ duration: 0 });
};

const toggle = async () => {
    if (isOpen()) {
        await close();
    } else {
        await open();
    }
};

const initialize = () => {
    const $sidebar = els.$sidebar();
    const $toggle = els.$toggle();

    $sidebar.attr(
        'data-open',
        isInDrawerMode() ? '0' : '1'
    );

    $toggle.on('click', (event) => {
        event.preventDefault();
        toggle();
    });

    $(window).on(
        'resize:horizontally',
        throttle(
            50,
            () => {
                if (!isInDrawerMode()) {
                    if (!isOpen()) {
                        openImmediately();
                    }
                } else {
                    if (isOpen()) {
                        closeImmediately();
                    }
                }
            }
        )
    );
};

export default {
    initialize,
    isInDrawerMode,
    open,
    close,
    toggle,
};

